import { useCallback, useEffect, useState } from 'react';
import type { Eu } from '../../acesso';
import type { Sessao } from '../../auth';
import { ErroDeArea } from '../../design/Estados';
import { reais } from '../../produtos';
import { juntar, mostraValor, somarReais, useValoresOcultos } from '../../design/sigilo';
import { TopoMovel } from '../../shell/ShellBalconista';
import { ROTULO_DO_PROGRAMA, cancelarVenda, hora, minhasVendas } from '../../vendas';
import type { MinhaVenda, ProgramaDeVenda } from '../../vendas';
import { carregadorDoUltimo, perguntaDoCancelamento } from './minhas-vendas';
import './registrar.css';

/**
 * AS VENDAS DA PESSOA — hoje, esta semana (sábado→sexta) e a passada. Cancelar a própria vale por
 * 24 h (regra do 1.0); cancelar não apaga: a venda fica riscada, com a hora do cancelamento.
 */

type Periodo = 'hoje' | 'semana' | 'semana_passada' | 'mes';
const PERIODOS: readonly { p: Periodo; rotulo: string }[] = [
  { p: 'hoje', rotulo: 'Hoje' },
  { p: 'semana', rotulo: 'Semana' },
  { p: 'semana_passada', rotulo: 'Passada' },
  { p: 'mes', rotulo: 'Mês' },
];
type Filtro = 'todos' | ProgramaDeVenda;
const FILTROS: readonly { f: Filtro; rotulo: string }[] = [
  { f: 'todos', rotulo: 'Todos' },
  { f: 'vitamina', rotulo: 'Vitamina' },
  { f: 'pre_vencido', rotulo: 'Pré-venc.' },
  { f: 'paradao', rotulo: 'Paradão' },
];

export function MinhasVendas({ eu, sessao }: { eu: Eu | null; sessao: Sessao }) {
  const ocultos = useValoresOcultos();
  const [periodo, setPeriodo] = useState<Periodo>('hoje');
  const [filtro, setFiltro] = useState<Filtro>('todos');
  const [vendas, setVendas] = useState<readonly MinhaVenda[] | null>(null);
  const [erro, setErro] = useState(false);
  const [aviso, setAviso] = useState<{ ok: boolean; texto: string } | null>(null);
  // cancelar pede confirmação na própria linha: um toque sem querer não tira a premiação
  const [confirmando, setConfirmando] = useState<string | null>(null);
  const [cancelando, setCancelando] = useState<string | null>(null);

  // só a resposta do ÚLTIMO pedido vale: troca rápida de período não mostra a lista errada
  const [pedir] = useState(() =>
    carregadorDoUltimo(minhasVendas, (r: Awaited<ReturnType<typeof minhasVendas>>) => {
      if (r.ok) setVendas(r.dados.vendas);
      else setErro(true);
    }),
  );
  const carregar = useCallback(() => {
    setErro(false);
    setVendas(null);
    void pedir(periodo);
  }, [periodo, pedir]);
  useEffect(carregar, [carregar]);

  // Separado por programa (dono, 25/09): a aba mostra só as do programa, com o total dele.
  const doFiltro = vendas?.filter((v) => filtro === 'todos' || v.programa === filtro) ?? null;
  const contagem = (f: Filtro) =>
    (vendas ?? [])
      .filter((v) => v.status === 'ativo' && (f === 'todos' || v.programa === f))
      .reduce((s, v) => s + v.quantidade, 0);
  const ativas = doFiltro?.filter((v) => v.status === 'ativo') ?? [];
  const itens = ativas.reduce((s, v) => s + v.quantidade, 0);
  // Soma em centavos inteiros (nada de float com dinheiro); oculto pelo servidor → sem total.
  const total = ocultos ? null : somarReais(ativas.map((v) => v.premioTotal));

  const cancelar = async (v: MinhaVenda) => {
    setCancelando(v.id);
    const r = await cancelarVenda(sessao, v.id);
    setCancelando(null);
    setConfirmando(null);
    setAviso(
      r.ok
        ? { ok: true, texto: `Venda cancelada: ${v.descricao}.` }
        : { ok: false, texto: r.mensagem },
    );
    if (r.ok) carregar();
  };

  return (
    <>
      <TopoMovel eu={eu} voltar="#/desempenho" titulo="Minhas vendas" />
      <div className="rv mv">
        <div className="mv-topo">
          <h1>Minhas vendas</h1>
          <div className="mv-periodos" role="tablist">
            {PERIODOS.map((x) => (
              <button
                key={x.p}
                type="button"
                role="tab"
                aria-selected={periodo === x.p}
                className={periodo === x.p ? 'mv-sel' : ''}
                onClick={() => setPeriodo(x.p)}
              >
                {x.rotulo}
              </button>
            ))}
          </div>
        </div>
        <div className="mv-filtros" role="tablist" aria-label="Programa">
          {FILTROS.map((x) => (
            <button
              key={x.f}
              type="button"
              role="tab"
              aria-selected={filtro === x.f}
              className={filtro === x.f ? 'mv-sel' : ''}
              onClick={() => setFiltro(x.f)}
            >
              {x.rotulo}
              {vendas !== null && <span className="mv-n">{contagem(x.f)}</span>}
            </button>
          ))}
        </div>
        {aviso && (
          <div
            className={aviso.ok ? 'mv-aviso mv-aviso-ok' : 'rv-recusado mv-aviso'}
            role={aviso.ok ? 'status' : 'alert'}
          >
            <span>{aviso.texto}</span>
          </div>
        )}
        {erro ? (
          <ErroDeArea oQue="suas vendas" aoTentar={carregar} />
        ) : (
          <section className="rv-hoje mv-lista" aria-busy={vendas === null}>
            <div className="rv-hoje-topo">
              <h2>
                {vendas === null
                  ? 'Carregando…'
                  : juntar([
                      `${String(itens)} ${itens === 1 ? 'item' : 'itens'}`,
                      total !== null && reais(total),
                    ])}
              </h2>
            </div>
            {doFiltro !== null && doFiltro.length === 0 && (
              <div className="rv-hoje-vazio">Nenhuma venda neste período.</div>
            )}
            {doFiltro?.map((v) => {
              const podeCancelar =
                v.status === 'ativo' &&
                Date.now() - new Date(v.registradoEm).getTime() < 24 * 3_600_000;
              return (
                <div
                  key={v.id}
                  className={`mv-linha${v.status === 'cancelado' ? ' rv-cancelada' : ''}`}
                >
                  <div className="mv-texto">
                    <div className="mv-nome">{v.descricao}</div>
                    <div className="mv-sub">
                      {periodo === 'hoje'
                        ? hora(v.registradoEm)
                        : new Date(v.registradoEm).toLocaleString('pt-BR', {
                            day: '2-digit',
                            month: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                            timeZone: 'America/Sao_Paulo',
                          })}{' '}
                      · {v.quantidade} un · {ROTULO_DO_PROGRAMA[v.programa]} ·{' '}
                      {reais(v.precoUnitario)}
                      {v.loteCodigo ? ` · lote ${v.loteCodigo}` : ''}
                      {v.status === 'cancelado' && v.canceladoEm
                        ? ` · cancelada às ${hora(v.canceladoEm)}`
                        : ''}
                    </div>
                  </div>
                  {mostraValor(ocultos, v.premioTotal) && (
                    <div className="mv-valor">
                      {v.status === 'cancelado' ? reais('0.00') : reais(v.premioTotal)}
                    </div>
                  )}
                  {podeCancelar && confirmando !== v.id && (
                    <button
                      type="button"
                      className="mv-cancelar"
                      onClick={() => {
                        setAviso(null);
                        setConfirmando(v.id);
                      }}
                    >
                      Cancelar
                    </button>
                  )}
                  {podeCancelar && confirmando === v.id && (
                    <div className="mv-confirma" role="group" aria-label="Confirmar cancelamento">
                      <span>{perguntaDoCancelamento(v)}</span>
                      <div className="mv-confirma-botoes">
                        <button
                          type="button"
                          className="mv-confirma-nao"
                          disabled={cancelando === v.id}
                          onClick={() => setConfirmando(null)}
                        >
                          Não
                        </button>
                        <button
                          type="button"
                          className="mv-confirma-sim"
                          disabled={cancelando === v.id}
                          onClick={() => void cancelar(v)}
                        >
                          {cancelando === v.id ? 'Cancelando…' : 'Sim, cancelar'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </section>
        )}
        <p className="rv-nota">
          A premiação aqui é a lançada; quem confirma e paga é a conferência de sábado. Cancelar
          vale por 24 horas depois do registro.
        </p>
      </div>
    </>
  );
}
